interface WeatherSummary {
  sample_count?: number | null;
  temperature_avg_c?: number | null;
  wind_avg_mps?: number | null;
  wind_max_mps?: number | null;
  overcast_max_pct?: number | null;
  fog_max_density?: number | null;
  precipitation_type?: string | null;
  air_pressure_hpa?: number | null;
}

const MPS_TO_KMH = 3.6;

function speedKmh(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined) return "—";
  return `${(value * MPS_TO_KMH).toFixed(digits)} km / h`;
}

function num(value: number | null | undefined, digits = 0, suffix = ""): string {
  if (value === null || value === undefined) return "—";
  return `${value.toFixed(digits)}${suffix}`;
}

function conditionLabel(w: WeatherSummary): string {
  const precip = w.precipitation_type;
  if (precip && !["None", "none", ""].includes(precip)) return precip;
  const cloud = w.overcast_max_pct;
  if (cloud != null && cloud > 60) return "Cloudy";
  if (cloud != null && cloud > 20) return "Partly Cloudy";
  if (w.fog_max_density != null && w.fog_max_density > 0.3) return "Foggy";
  return "Clear";
}

function WeatherIcon({ summary }: { summary: WeatherSummary }) {
  const precip = summary.precipitation_type;
  const cloud = summary.overcast_max_pct;
  const hasPrecip = precip && !["None", "none", ""].includes(precip);
  if (hasPrecip) {
    return (
      <svg viewBox="0 0 64 64" fill="none">
        <path
          d="M20 28a12 12 0 0 1 23.4-3.6A9 9 0 0 1 46 42H18a8 8 0 0 1-.5-16z"
          fill="rgba(100,160,210,0.25)"
          stroke="rgba(140,190,230,0.7)"
          strokeWidth="2"
        />
        <line x1="22" y1="48" x2="20" y2="56" stroke="rgba(100,180,255,0.6)" strokeWidth="2" strokeLinecap="round" />
        <line x1="32" y1="48" x2="30" y2="56" stroke="rgba(100,180,255,0.6)" strokeWidth="2" strokeLinecap="round" />
        <line x1="42" y1="48" x2="40" y2="56" stroke="rgba(100,180,255,0.6)" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }
  if (cloud != null && cloud > 60) {
    return (
      <svg viewBox="0 0 64 64" fill="none">
        <path
          d="M20 30a12 12 0 0 1 23.4-3.6A9 9 0 0 1 46 44H18a8 8 0 0 1-.5-16z"
          fill="rgba(120,140,160,0.3)"
          stroke="rgba(160,180,200,0.6)"
          strokeWidth="2"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 64 64" fill="none">
      <circle cx="32" cy="32" r="12" fill="rgba(255,200,50,0.2)" stroke="rgba(255,200,80,0.7)" strokeWidth="2" />
      <line x1="32" y1="12" x2="32" y2="18" stroke="rgba(255,200,80,0.5)" strokeWidth="2" strokeLinecap="round" />
      <line x1="32" y1="46" x2="32" y2="52" stroke="rgba(255,200,80,0.5)" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="32" x2="18" y2="32" stroke="rgba(255,200,80,0.5)" strokeWidth="2" strokeLinecap="round" />
      <line x1="46" y1="32" x2="52" y2="32" stroke="rgba(255,200,80,0.5)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

export function WeatherCard({
  summary,
  compact = false,
}: {
  summary: WeatherSummary;
  compact?: boolean;
}) {
  const temp = summary.temperature_avg_c;
  const windAvg = summary.wind_avg_mps;
  const windMax = summary.wind_max_mps;
  const cloud = summary.overcast_max_pct;
  const fog = summary.fog_max_density;
  const pressure = summary.air_pressure_hpa;

  if (compact) {
    return (
      <div className="weather-compact">
        <div className="weather-compact__hero">
          <div className="weather-compact__icon">
            <WeatherIcon summary={summary} />
          </div>
          <div className="weather-compact__temp">
            <span className="weather-compact__temp-value">
              {temp != null ? temp.toFixed(0) : "—"}
            </span>
            <span className="weather-compact__temp-unit">°C</span>
          </div>
          <div className="weather-compact__cond">{conditionLabel(summary)}</div>
        </div>
        <dl className="weather-compact__stats">
          <div>
            <dt>Wind</dt>
            <dd>{speedKmh(windAvg)}</dd>
            {windMax != null && <small>Peak {speedKmh(windMax)}</small>}
          </div>
          <div>
            <dt>Cloud</dt>
            <dd>{num(cloud, 0, "%")}</dd>
          </div>
          <div>
            <dt>Pressure</dt>
            <dd>{num(pressure, 0)} hPa</dd>
          </div>
          <div>
            <dt>Fog</dt>
            <dd>{num(fog, 2)}</dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="weather-card">
      <div className="weather-card-hero">
        <div className="weather-card-icon">
          <WeatherIcon summary={summary} />
        </div>
        <div className="weather-card-temp">
          <span className="weather-temp-value">{temp != null ? temp.toFixed(1) : "—"}</span>
          <span className="weather-temp-unit">°C</span>
        </div>
        <div className="weather-card-condition">{conditionLabel(summary)}</div>
      </div>

      <div className="weather-card-details">
        <div className="weather-tile">
          <div className="weather-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.7 7.7A2.5 2.5 0 1 1 19 12H2" />
              <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
              <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
            </svg>
          </div>
          <span className="weather-tile-label">Wind</span>
          <span className="weather-tile-value">{speedKmh(windAvg)}</span>
          <div className="weather-tile-bar">
            <div
              className="weather-tile-bar-fill weather-tile-bar--wind"
              style={{ width: `${clamp((windAvg ?? 0) * 3.6 / 60 * 100, 0, 100)}%` }}
            />
          </div>
          <span className="weather-tile-sub">Peak {speedKmh(windMax)}</span>
        </div>

        <div className="weather-tile">
          <div className="weather-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 19a4.5 4.5 0 0 1-.5-9A6 6 0 0 1 17.5 8.5 3.5 3.5 0 0 1 19 15.5H6z" />
            </svg>
          </div>
          <span className="weather-tile-label">Cloud Cover</span>
          <span className="weather-tile-value">{num(cloud, 0, "%")}</span>
          <div className="weather-tile-bar">
            <div
              className="weather-tile-bar-fill weather-tile-bar--cloud"
              style={{ width: `${clamp(cloud ?? 0, 0, 100)}%` }}
            />
          </div>
        </div>

        <div className="weather-tile">
          <div className="weather-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <span className="weather-tile-label">Pressure</span>
          <span className="weather-tile-value">
            {num(pressure, 0)} <small>hPa</small>
          </span>
          <div className="weather-tile-bar">
            <div
              className="weather-tile-bar-fill weather-tile-bar--pressure"
              style={{ width: `${clamp(((pressure ?? 1013) - 980) / 60 * 100, 0, 100)}%` }}
            />
          </div>
        </div>

        <div className="weather-tile">
          <div className="weather-tile-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M4 10h16" />
              <path d="M6 14h12" />
              <path d="M8 18h8" />
              <path d="M5 6h14" />
            </svg>
          </div>
          <span className="weather-tile-label">Fog Density</span>
          <span className="weather-tile-value">{num(fog, 2)}</span>
          <div className="weather-tile-bar">
            <div
              className="weather-tile-bar-fill weather-tile-bar--fog"
              style={{ width: `${clamp((fog ?? 0) * 100, 0, 100)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
